import { action, atom, batch, onConnect, reaction } from '@reatom/framework'
import i18next from 'i18next'

import { AsyncStateN, Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { showSnackbarAction } from '@root/shared/ui-kit'

import {
  approveEvmAllowanceAction,
  checkAllowanceRequestAction,
} from '../../allowance/atoms/reatom-evm-allowance'

import { tradingPairAddressAtom, tradingPairAtom } from './trading-pair'
import { tradingWalletAtom, tradingWalletIdAtom } from './trading-wallet'

import type { ApproveEvmAllowanceError } from '../../allowance/atoms/reatom-evm-allowance'
import type { TradeCheckAllowanceListData } from '@blade/trade-api-client'
import type { Error } from '@root/shared'

export const activeTradingPairAllowanceAtom = atom<
  AsyncStateN.AsyncStateN<Error.UnknownError, TradeCheckAllowanceListData>
>(AsyncStateN.success({ tokens: [] }), 'activeTradingPairAllowanceAtom')

export const loadActivePairAllowanceAction = action(async (ctx) => {
  const tradingPair = ctx.get(tradingPairAtom)
  const wallet = ctx.get(tradingWalletAtom)

  if (!tradingPair || !wallet) {
    return
  }

  if (!Network.isEvmNetwork(tradingPair.base.network)) {
    return
  }

  activeTradingPairAllowanceAtom(ctx, AsyncStateN.loading())
  const result = await ctx.schedule(() =>
    checkAllowanceRequestAction(ctx, {
      network: tradingPair.base.network,
      tokens: [tradingPair.base.address, tradingPair.quote.address],
      walletAddress: wallet.address,
    }),
  )

  if (Result.isFailure(result)) {
    showSnackbarAction(ctx, {
      text: i18next.t('trading.check-approval-error'),
      intent: 'error',
    })
  }

  activeTradingPairAllowanceAtom(ctx, AsyncStateN.fromResult(result))
}, 'loadActivePairAllowanceAction')

const allowanceReaction = reaction(async (ctx) => {
  const _tradingPairAddress = ctx.spy(tradingPairAddressAtom)
  const _walletId = ctx.spy(tradingWalletIdAtom)
  activeTradingPairAllowanceAtom(ctx, AsyncStateN.success({ tokens: [] }))
  activePairApproveAllowanceStateAtom(ctx, AsyncStateN.notInitiated())

  if (!_tradingPairAddress || !_walletId) {
    return
  }
  loadActivePairAllowanceAction(ctx)
}, 'allowanceReaction')

onConnect(activeTradingPairAllowanceAtom, (ctx) => {
  const sub = allowanceReaction(ctx)
  return () => sub.unsubscribe()
})

export const activePairApproveAllowanceStateAtom = atom<
  AsyncStateN.AsyncStateN<ApproveEvmAllowanceError, unknown>
>(AsyncStateN.notInitiated(), 'activePairApproveAllowanceStateAtom')

export const approveActivePairAllowanceAction = action(async (ctx) => {
  const state = ctx.get(activeTradingPairAllowanceAtom)
  const tradingPair = ctx.get(tradingPairAtom)
  const wallet = ctx.get(tradingWalletAtom)

  if (!tradingPair || !wallet || !state.value) {
    return
  }
  const currenciesToApprove = state.value.tokens.filter(
    (token) => !token.isApproved,
  )
  if (currenciesToApprove.length === 0) {
    return
  }

  activePairApproveAllowanceStateAtom(ctx, AsyncStateN.loading())

  const result = await ctx.schedule(() =>
    approveEvmAllowanceAction(ctx, {
      currencies: currenciesToApprove.map((token) => token.address),
      wallet,
    }),
  )

  batch(ctx, () => {
    activePairApproveAllowanceStateAtom(ctx, AsyncStateN.fromResult(result))
    if (Result.isSuccess(result)) {
      loadActivePairAllowanceAction(ctx)
      showSnackbarAction(ctx, {
        text: i18next.t('trading.tokens-approved-message'),
        intent: 'success',
      })
    } else {
      if (
        '_tag' in result.failure &&
        result.failure._tag === 'InsufficientBalance'
      ) {
        showSnackbarAction(ctx, {
          text: i18next.t('trading.tokens-approve-error'),
          description: i18next.t('trading.tokens-approve-error.insufficient'),
          intent: 'error',
        })
      }

      showSnackbarAction(ctx, {
        text: i18next.t('common.something-went-wrong'),
        intent: 'error',
      })
    }
  })
}, 'approveActivePairAllowanceAction')
